import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import useActivation from './hooks/useActivation';
import PhotoManagerList from './PhotoManagerList';
import BulkUploadMedia from '@/src/Common/components/BulkUploadMedia';
import Loading from '@/src/Common/components/Loading';
import ZipPrompt from './ZipPrompt';
import DownloadRequestList from './DownloadRequestList';
import PhotoManagerFilters from './PhotoManagerFilters';
import SharePrompt from './SharePrompt';
import useGetGalleries from '@/src/GalleryConfiguration/components/hooks/useGetGalleries';
import UniqueFacesList from './UniqueFacesList';
import useActivationDevices from './hooks/useActivationDevices';
import { RefreshCw } from 'lucide-react';

const AUTO_REFRESH_KEY = 'photoManager.autoRefresh';

export default function PhotoManager() {
    const [showHidden, setShowHidden] = useState(false);
    const { activationId } = useParams();
    const [isLoading, setIsLoading] = useState(true);
    const [gallery, setGallery] = useState<GalleryObject | null>(null);
    const [photos, setPhotos] = useState<MediaInstance[]>([]);
    const [allPhotos, setAllPhotos] = useState<MediaInstance[]>([]);
    const [typeFilter, setTypeFilter] = useState<string[]>([]);
    const [deviceFilter, setDeviceFilter] = useState<string | null>(null); // null = all devices, 'uploaded' = no device, or deviceKeyId
    const [groupFilter, setGroupFilter] = useState(''); // matches media.groupKey (e.g. a team's registration code)
    const [tagFilter, setTagFilter] = useState<string | null>(null); // single media_tag, picked from tags in use
    const [isShowingBulkUpload, setIsShowingBulkUpload] = useState(false);
    const [isShowingZipPrompt, setIsShowingZipPrompt] = useState(false);
    const [isShowingSharePrompt, setIsShowingSharePrompt] = useState(false);
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
    const [selectedShareIdentifiers, setSelectedShareIdentifiers] = useState<
        string[]
    >([]);
    const [galleryId, setGalleryId] = useState<number | null>(null);
    const { getActivation, getActivationPhotos } = useActivation();
    const { getGalleriesForActivation } = useGetGalleries();
    const { devices: activationDevices } = useActivationDevices(activationId ? +activationId : 0);

    const [columnCount, setColumnCount] = useState(4);
    const [newPhotosCount, setNewPhotosCount] = useState(0);
    const [autoRefresh, setAutoRefresh] = useState(() => {
        const saved = localStorage.getItem(AUTO_REFRESH_KEY);
        return saved === 'true';
    });
    const lastKnownPhotoCount = useRef(0);
    const lastKnownMaxId = useRef(0);

    // Persist auto-refresh setting
    useEffect(() => {
        localStorage.setItem(AUTO_REFRESH_KEY, autoRefresh.toString());
    }, [autoRefresh]);

    // Function to load photos
    const loadPhotos = useCallback(async (isInitialLoad = false) => {
        try {
            const activation = await getActivation(
                activationId ? +activationId : 0,
            );

            if (isInitialLoad) {
                const galleries = await getGalleriesForActivation(
                    activation.id,
                );

                setGalleryId(galleries[0].id);
                setGallery(galleries[0]);
            }

            const photos = await getActivationPhotos(
                activation.id,
                typeFilter,
            );

            if (isInitialLoad) {
                setAllPhotos(photos);
                lastKnownPhotoCount.current = photos.length;
                lastKnownMaxId.current = photos.length > 0 
                    ? Math.max(...photos.map((p: MediaInstance) => p.id)) 
                    : 0;
            }

            return photos;
        } catch (e) {
            console.error(e);
            return null;
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activationId, getActivation, getActivationPhotos, getGalleriesForActivation, typeFilter]);

    // Initial load
    useEffect(() => {
        let isMounted = true;
        const load = async () => {
            try {
                await loadPhotos(true);
            } catch (e) {
                console.error(e);
            } finally {
                isMounted && setIsLoading(false);
            }
        };

        load();

        return () => {
            isMounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Polling for new photos
    useEffect(() => {
        if (isLoading) return;

        const checkForNewPhotos = async () => {
            try {
                const latestPhotos = await loadPhotos(false);
                if (latestPhotos) {
                    const latestMaxId = latestPhotos.length > 0 
                        ? Math.max(...latestPhotos.map((p: MediaInstance) => p.id)) 
                        : 0;
                    
                    // Check if there are new photos
                    let newCount = 0;
                    if (latestMaxId > lastKnownMaxId.current) {
                        newCount = latestPhotos.filter(
                            (p: MediaInstance) => p.id > lastKnownMaxId.current
                        ).length;
                    } else if (latestPhotos.length > lastKnownPhotoCount.current) {
                        newCount = latestPhotos.length - lastKnownPhotoCount.current;
                    }

                    if (newCount > 0) {
                        if (autoRefresh) {
                            // Auto-refresh: update the photos immediately
                            setAllPhotos(latestPhotos);
                            lastKnownPhotoCount.current = latestPhotos.length;
                            lastKnownMaxId.current = latestMaxId;
                            setNewPhotosCount(0);
                        } else {
                            // Manual refresh: just show the count
                            setNewPhotosCount(newCount);
                        }
                    }
                }
            } catch (e) {
                console.error('Error checking for new photos:', e);
            }
        };

        // Poll every 10 seconds
        const interval = setInterval(checkForNewPhotos, 10000);

        return () => clearInterval(interval);
    }, [isLoading, loadPhotos, autoRefresh]);

    // Function to refresh and show new photos
    const handleRefreshPhotos = async () => {
        setIsLoading(true);
        try {
            const photos = await loadPhotos(false);
            if (photos) {
                setAllPhotos(photos);
                lastKnownPhotoCount.current = photos.length;
                lastKnownMaxId.current = photos.length > 0 
                    ? Math.max(...photos.map((p: MediaInstance) => p.id)) 
                    : 0;
                setNewPhotosCount(0);
            }
        } catch (e) {
            console.error(e);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        let sortedPhotos = [...allPhotos].sort((a, b) => {
            return sortOrder === 'asc' ? a.id - b.id : b.id - a.id;
        });

        if (!showHidden) {
            sortedPhotos = sortedPhotos.filter(
                (photo: MediaInstance) => !photo.isHidden,
            );
        }

        // Only apply type filter if specific types are selected (not empty = all types)
        if (typeFilter.length > 0) {
            sortedPhotos = sortedPhotos.filter((photo: MediaInstance) =>
                typeFilter.includes(photo.smileportType || 'processed'),
            );
        }

        // Apply device filter
        if (deviceFilter !== null) {
            if (deviceFilter === 'uploaded') {
                // Show photos without a device (uploaded manually)
                sortedPhotos = sortedPhotos.filter(
                    (photo: MediaInstance) => !photo.deviceKeyId,
                );
            } else {
                // Show photos from a specific device
                sortedPhotos = sortedPhotos.filter(
                    (photo: MediaInstance) => photo.deviceKeyId === deviceFilter,
                );
            }
        }

        // Apply group filter (team/registration code stamped by booths)
        if (groupFilter.trim().length > 0) {
            const needle = groupFilter.trim().toLowerCase();
            sortedPhotos = sortedPhotos.filter((photo: MediaInstance) =>
                (photo.groupKey ?? '').toLowerCase().includes(needle),
            );
        }

        // Apply tag filter (media_tag rows ride along on each photo)
        if (tagFilter) {
            sortedPhotos = sortedPhotos.filter((photo: MediaInstance) =>
                (photo.tags ?? []).includes(tagFilter),
            );
        }

        setPhotos(sortedPhotos);
    }, [allPhotos, typeFilter, showHidden, sortOrder, deviceFilter, groupFilter, tagFilter]);

    useEffect(() => {
        if (sortOrder === 'asc') {
            setPhotos(
                [...photos].sort((a, b) => {
                    return a.id - b.id;
                }),
            );
        } else {
            setPhotos(
                [...photos].sort((a, b) => {
                    return b.id - a.id;
                }),
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sortOrder]);

    const [refreshCount, setRefreshCount] = useState(0);

    if (isLoading) {
        return <Loading isLoading={true} />;
    }

    return (
        <div>
            {/* New photos notification banner */}
            {newPhotosCount > 0 && (
                <div className="sticky top-0 z-50 bg-blue-600 text-white px-4 py-3 shadow-lg">
                    <div className="flex items-center justify-center gap-4">
                        <span className="font-medium">
                            {newPhotosCount} new {newPhotosCount === 1 ? 'photo' : 'photos'} available
                        </span>
                        <button
                            onClick={handleRefreshPhotos}
                            className="flex items-center gap-2 bg-white text-blue-600 px-4 py-1.5 rounded-md font-medium hover:bg-blue-50 transition-colors"
                        >
                            <RefreshCw className="h-4 w-4" />
                            Refresh
                        </button>
                    </div>
                </div>
            )}
            <DownloadRequestList
                key={refreshCount}
                activationId={activationId ? +activationId : 0}
            />
            <PhotoManagerFilters
                photos={photos}
                onFilterChange={setTypeFilter}
                onSortChange={setSortOrder}
                activationId={activationId ? +activationId : 0}
                isShowingZipPrompt={isShowingZipPrompt}
                setIsShowingZipPrompt={setIsShowingZipPrompt}
                isShowingBulkUpload={isShowingBulkUpload}
                setIsShowingBulkUpload={setIsShowingBulkUpload}
                showHidden={showHidden}
                setShowHidden={setShowHidden}
                sortOrder={sortOrder}
                setSortOrder={setSortOrder}
                selectedShareIdentifiers={selectedShareIdentifiers}
                setPhotos={setPhotos}
                setSelectedShareIdentifiers={setSelectedShareIdentifiers}
                setColumnCount={setColumnCount}
                columnCount={columnCount}
                setIsShowingSharePrompt={setIsShowingSharePrompt}
                deviceFilter={deviceFilter}
                setDeviceFilter={setDeviceFilter}
                tagFilter={tagFilter}
                setTagFilter={setTagFilter}
                activationDevices={activationDevices}
                allPhotos={allPhotos}
                autoRefresh={autoRefresh}
                setAutoRefresh={setAutoRefresh}
                onMediaEdited={handleRefreshPhotos}
                galleryId={galleryId}
            />
            {allPhotos.some((photo) => photo.groupKey) && (
                <div className="mx-4 my-2 flex items-center gap-2">
                    <label className="text-sm font-medium text-gray-700">
                        Group / team
                    </label>
                    <input
                        type="text"
                        value={groupFilter}
                        onChange={(e) => setGroupFilter(e.target.value)}
                        placeholder="Filter by team code…"
                        className="rounded-md border border-gray-300 px-2 py-1 text-sm"
                    />
                    {groupFilter && (
                        <button
                            type="button"
                            onClick={() => setGroupFilter('')}
                            className="text-sm text-gray-500 hover:text-gray-700"
                        >
                            Clear
                        </button>
                    )}
                </div>
            )}
            <UniqueFacesList
                activationId={activationId ? +activationId : 0}
                baseUrl={gallery?.baseUrl ?? ''}
                slug={gallery?.slug ?? ''}
            />
            <PhotoManagerList
                photos={photos}
                setSelectedShareIdentifiers={setSelectedShareIdentifiers}
                selectedShareIdentifiers={selectedShareIdentifiers}
                columnCount={columnCount}
                baseUrl={gallery?.baseUrl ?? null}
                slug={gallery?.slug ?? null}
                activationDevices={activationDevices}
            />

            {isShowingBulkUpload ? (
                <BulkUploadMedia
                    onCancel={() => setIsShowingBulkUpload(false)}
                    onComplete={() => {
                        setIsShowingBulkUpload(false);
                        handleRefreshPhotos();
                    }}
                    activationId={activationId ? +activationId : 0}
                    galleryId={galleryId}
                />
            ) : null}
            {isShowingZipPrompt ? (
                <ZipPrompt
                    shareIdentifiers={photos.map(
                        (photo) => photo.shareIdentifier,
                    )}
                    activationId={activationId ? +activationId : 0}
                    onComplete={() => {
                        setIsShowingZipPrompt(false);
                        setRefreshCount(refreshCount + 1);
                    }}
                />
            ) : null}
            {isShowingSharePrompt ? (
                <SharePrompt
                    shareIdentifiers={selectedShareIdentifiers}
                    activationId={activationId ? +activationId : 0}
                    galleryId={galleryId ? +galleryId : 0}
                    onComplete={() => {
                        setIsShowingSharePrompt(false);
                        setSelectedShareIdentifiers([]);
                    }}
                />
            ) : null}
        </div>
    );
}
